import { useState } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  Badge,
  Table,
  Tooltip,
  Code,
  Switch,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconArrowUp,
  IconArrowDown,
  IconPuzzle,
} from '@tabler/icons-react';
import { UseFormReturnType } from '@mantine/form';
import {
  WebBotProviderData,
  ExtensionSlotConfig,
  ExtensionLayoutConfig,
  ExtensionDisplayMode,
  ExtensionResponsiveMode,
  ExtensionPosition,
  ExtensionLayoutMode,
} from '@/API';
import { ExtensionModal } from './ExtensionModal';

interface ExtensionListProps {
  form: UseFormReturnType<WebBotProviderData>;
  availableBundles: string[];
}

const positionColorMap: Record<string, string> = {
  top: 'cyan',
  bottom: 'orange',
  left: 'blue',
  right: 'teal',
};

const positionLabelMap: Record<string, string> = {
  top: '頂部 (Top)',
  bottom: '底部 (Bottom)',
  left: '左側 (Left)',
  right: '右側 (Right)',
};

export function ExtensionList({ form, availableBundles }: ExtensionListProps) {
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const extensions: ExtensionSlotConfig[] = form.values.extensions || [];

  const handleOpenAdd = () => {
    setEditingIndex(null);
    openModal();
  };

  const handleOpenEdit = (index: number) => {
    setEditingIndex(index);
    openModal();
  };

  const handleSaveExtension = (item: ExtensionSlotConfig) => {
    const currentList = [...extensions];
    if (editingIndex !== null && editingIndex >= 0) {
      currentList[editingIndex] = item;
    } else {
      item.order = currentList.length;
      currentList.push(item);
    }
    form.setFieldValue('extensions', currentList);
  };

  const handleDelete = (index: number) => {
    const target = extensions[index];
    modals.openConfirmModal({
      title: '確認刪除擴充區',
      children: (
        <Text size="sm">
          確定要刪除擴充區 <Code>{target?.name || target?.id}</Code> 嗎？
        </Text>
      ),
      labels: { confirm: '刪除', cancel: '取消' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        const nextList = extensions.filter((_, i) => i !== index);
        nextList.forEach((c, i) => {
          c.order = i;
        });
        form.setFieldValue('extensions', nextList);
      },
    });
  };

  const handleToggleEnabled = (index: number, checked: boolean) => {
    const nextList = [...extensions];
    const target = nextList[index];
    const std: ExtensionLayoutConfig = target.standard
      ? { ...target.standard, enabled: checked }
      : { enabled: checked, position: ExtensionPosition.Left, layoutMode: ExtensionLayoutMode.FloatingDock };
    nextList[index] = { ...target, standard: std };
    form.setFieldValue('extensions', nextList);
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= extensions.length) return;
    const nextList = [...extensions];
    const [moved] = nextList.splice(index, 1);
    nextList.splice(targetIndex, 0, moved);
    nextList.forEach((c, i) => {
      c.order = i;
    });
    form.setFieldValue('extensions', nextList);
  };

  const currentEditingItem = editingIndex !== null ? extensions[editingIndex] : null;

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <div>
            <Group gap="xs">
              <IconPuzzle size={20} />
              <Text fw={600} size="md">
                擴充區設定
              </Text>
            </Group>
            <Text size="xs" c="dimmed" mt={2}>
              配置聊天視窗四周 (Top/Bottom/Left/Right) 的動畫看板、資訊橫幅或外部 Slot 擴充元件
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            size="xs"
            variant="filled"
            onClick={handleOpenAdd}
          >
            新增擴充區
          </Button>
        </Group>

        {extensions.length === 0 ? (
          <Paper withBorder p="xl" bg="gray.0" radius="sm">
            <Text c="dimmed" size="sm" ta="center">
              目前尚未配置任何擴充區。點擊右上角「新增擴充區」開始配置。
            </Text>
          </Paper>
        ) : (
          <Table withTableBorder withColumnBorders highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 80 }}>啟用</Table.Th>
                <Table.Th style={{ width: 140 }}>方位 (一般 / 全螢幕)</Table.Th>
                <Table.Th>Slot ID / 名稱</Table.Th>
                <Table.Th>關聯資產包</Table.Th>
                <Table.Th>佈局模式 / 適應</Table.Th>
                <Table.Th style={{ width: 140, textAlign: 'center' }}>操作</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {extensions.map((ext, index) => {
                const stdPosKey = (ext.standard?.position || 'left').toLowerCase();
                const isStdWindowPanel =
                  ext.standard?.layoutMode === ExtensionLayoutMode.WindowPanel ||
                  (ext.standard?.layoutMode as any) === 'window-panel';
                const isEnabled = ext.standard?.enabled ?? true;
                const fs = ext.fullscreen;
                const fsPosKey = fs?.position ? fs.position.toLowerCase() : null;
                const isFsWindowPanel = fs?.layoutMode
                  ? fs.layoutMode === ExtensionLayoutMode.WindowPanel ||
                    (fs.layoutMode as any) === 'window-panel'
                  : null;

                return (
                  <Table.Tr key={ext.id || index}>
                    <Table.Td>
                      <Switch
                        size="sm"
                        checked={isEnabled}
                        onChange={(e) => handleToggleEnabled(index, e.currentTarget.checked)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        <Badge color={positionColorMap[stdPosKey] || 'gray'} variant="light" size="sm">
                          {positionLabelMap[stdPosKey] || ext.standard?.position || '左側 (Left)'}
                        </Badge>
                        {fsPosKey && fsPosKey !== stdPosKey && (
                          <Badge color={positionColorMap[fsPosKey] || 'gray'} variant="outline" size="xs">
                            全螢幕: {positionLabelMap[fsPosKey] || fs?.position}
                          </Badge>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Code fw={600}>slot="extension-{ext.id}"</Code>
                        {ext.name && (
                          <Text size="xs" c="dimmed">
                            ({ext.name})
                          </Text>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {ext.bundlePath ? (
                        (() => {
                          const rootBundle = ext.bundlePath.split('/')[0];
                          const exists = availableBundles.includes(rootBundle);
                          return exists ? (
                            <Badge variant="outline" color="blue">
                              {ext.bundlePath}
                            </Badge>
                          ) : (
                            <Tooltip label="此資產包在雲端儲存庫中已不存在">
                              <Badge variant="outline" color="red">
                                {ext.bundlePath} (資料不存在)
                              </Badge>
                            </Tooltip>
                          );
                        })()
                      ) : (
                        <Text size="xs" c="dimmed">
                          未指定 (僅 Slot 外部注入)
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Badge
                          size="xs"
                          variant="filled"
                          color={isStdWindowPanel ? 'indigo' : 'gray'}
                        >
                          {isStdWindowPanel ? '視窗面板' : '外部浮動'}
                        </Badge>
                        {fs && isFsWindowPanel !== null && isFsWindowPanel !== isStdWindowPanel && (
                          <Badge
                            size="xs"
                            variant="outline"
                            color={isFsWindowPanel ? 'indigo' : 'gray'}
                          >
                            全螢幕: {isFsWindowPanel ? '視窗面板' : '外部浮動'}
                          </Badge>
                        )}
                        <Badge size="xs" variant="dot" color={ext.displayMode === ExtensionDisplayMode.Persistent ? 'grape' : 'gray'}>
                          {ext.displayMode === ExtensionDisplayMode.Persistent ? '常駐' : '開窗'}
                        </Badge>
                        <Badge size="xs" variant="dot" color={ext.responsiveMode === ExtensionResponsiveMode.Stack ? 'orange' : 'gray'}>
                          {ext.responsiveMode === ExtensionResponsiveMode.Stack ? '手機堆疊' : '手機隱藏'}
                        </Badge>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="center">
                        <Tooltip label="上移">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            disabled={index === 0}
                            onClick={() => handleMove(index, 'up')}
                          >
                            <IconArrowUp size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="下移">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            disabled={index === extensions.length - 1}
                            onClick={() => handleMove(index, 'down')}
                          >
                            <IconArrowDown size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="編輯">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="blue"
                            onClick={() => handleOpenEdit(index)}
                          >
                            <IconEdit size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="刪除">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="red"
                            onClick={() => handleDelete(index)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}

        <ExtensionModal
          opened={modalOpened}
          onClose={closeModal}
          onSave={handleSaveExtension}
          initialValue={currentEditingItem}
          availableBundles={availableBundles}
        />
      </Stack>
    </Paper>
  );
}
